/**
 * ActStage — record the consumer's INTENDED NEXT STEP.
 *
 * IMPORTANT — Altara boundary:
 *   Altara is the trust and conduct layer AROUND the financial decision.
 *   It does NOT execute product acceptance, contracting, signatures,
 *   payment, application submission, adviser chat or fulfilment.
 *   This stage records the consumer's intended next step only. The
 *   institution continues its normal advice/contracting/fulfilment
 *   process through its own channels.
 *
 * Three responses (per product spec):
 *   • READY_TO_PROCEED           → decision COMPLETED. NOT an acceptance.
 *   • MORE_INFORMATION_REQUESTED → decision STAYS ACTIVE with a
 *                                  structured multi-select of topics.
 *   • DO_NOT_PROCEED             → decision COMPLETED with optional
 *                                  single reason.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import {
  CheckCircle2, HelpCircle, XCircle, ArrowRight, ArrowLeft, Info, Shield,
  FileText, ExternalLink,
} from "lucide-react";
import { NAVY, GOLD } from "@/components/wallet/WalletShell";
import {
  recordAct, CONSUMER_RESPONSE, MORE_INFO_TOPICS, DO_NOT_PROCEED_REASONS,
} from "@/lib/wallet-decisions";
import NaviFace from "@/components/wallet/NaviFace";

export default function ActStage({ decision, deviceId, onAdvance }) {
  const isCompleted    = decision.status === "COMPLETED";
  const isMoreInfoDone = decision.status === "MORE_INFORMATION_REQUESTED";
  const alreadyActed   = Boolean(decision.act);

  // Mode machine: null | 'READY_CONFIRM' | 'MORE_INFO_TOPICS' | 'DO_NOT_REASON'
  const [mode, setMode] = useState(null);

  if (isCompleted)    return <CompletedView decision={decision} deviceId={deviceId} />;
  if (isMoreInfoDone && alreadyActed) return <MoreInfoRecorded decision={decision} />;

  return (
    <section className="space-y-4" data-testid="act-stage">
      {/* Header — no more "record your decision" */}
      <div className="rounded-2xl border border-slate-100 bg-white p-4 flex items-start gap-3">
        <NaviFace size="md" />
        <div className="min-w-0">
          <div className="font-display text-[15px] font-semibold" style={{ color: NAVY }}>
            What would you like to do next?
          </div>
          <div className="text-[12px] text-slate-600 mt-1 leading-relaxed">
            You've reviewed the important information. The decision remains yours.
          </div>
        </div>
      </div>

      {mode === null && (
        <ChoiceGrid
          onReady={() => setMode("READY_CONFIRM")}
          onMoreInfo={() => setMode("MORE_INFO_TOPICS")}
          onDoNot={() => setMode("DO_NOT_REASON")}
        />
      )}
      {mode === "READY_CONFIRM"     && <ReadyConfirm      decision={decision} deviceId={deviceId} onCancel={() => setMode(null)} onAdvance={onAdvance} />}
      {mode === "MORE_INFO_TOPICS"  && <MoreInfoTopics    decision={decision} deviceId={deviceId} onCancel={() => setMode(null)} onAdvance={onAdvance} />}
      {mode === "DO_NOT_REASON"     && <DoNotProceedReason decision={decision} deviceId={deviceId} onCancel={() => setMode(null)} onAdvance={onAdvance} />}

      <BoundaryNotice />
    </section>
  );
}

/* ─────────────────── choice grid ─────────────────── */
function ChoiceGrid({ onReady, onMoreInfo, onDoNot }) {
  const choices = [
    { key: "READY",     title: "I'm ready to proceed",       body: "I've reviewed the information and I'm ready to continue with the institution.", icon: CheckCircle2, color: "#059669", onClick: onReady },
    { key: "MORE_INFO", title: "I need more information",    body: "I have questions or need more information before I decide.",                   icon: HelpCircle,   color: GOLD,      onClick: onMoreInfo },
    { key: "DO_NOT",    title: "I don't wish to proceed",    body: "I don't want to continue with this proposal.",                                 icon: XCircle,      color: "#B91C1C", onClick: onDoNot },
  ];
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="act-choices">
      {choices.map(c => (
        <button
          key={c.key}
          onClick={c.onClick}
          data-testid={`act-choice-${c.key.toLowerCase()}`}
          className="text-left rounded-2xl border border-slate-200 bg-white hover:shadow-md hover:border-slate-300 transition p-4 lg:p-5"
        >
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${c.color}22` }}>
            <c.icon className="w-5 h-5" style={{ color: c.color }} />
          </div>
          <div className="mt-3 font-display text-[15px] font-semibold" style={{ color: NAVY }}>{c.title}</div>
          <div className="text-[11.5px] text-slate-600 mt-1 leading-relaxed">{c.body}</div>
        </button>
      ))}
    </div>
  );
}

/* ─────────────────── option 1 · Ready confirm ─────────────────── */
function ReadyConfirm({ decision, deviceId, onCancel, onAdvance }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  async function confirm() {
    if (busy) return;
    setBusy(true); setErr(null);
    try {
      const next = await recordAct(decision.decision_id, deviceId, { choice: CONSUMER_RESPONSE.READY });
      onAdvance(next);
    } catch (e) { setErr(e?.response?.data?.detail || "Could not record response."); }
    finally { setBusy(false); }
  }
  return (
    <div className="rounded-2xl border p-5" style={{ background: "#ECFDF5", borderColor: "#A7F3D0" }} data-testid="act-ready-confirm">
      <div className="flex items-center gap-2">
        <CheckCircle2 className="w-5 h-5" style={{ color: "#059669" }} />
        <div className="text-[10.5px] uppercase tracking-[0.24em] font-semibold" style={{ color: "#059669" }}>
          Ready to continue
        </div>
      </div>
      <div className="mt-2 font-display text-[15.5px] font-semibold" style={{ color: NAVY }}>
        You've indicated that you're ready to continue with {decision.institution_name}.
      </div>
      <div className="text-[12px] text-slate-700 mt-2 leading-relaxed">
        This does not accept or sign the proposal through Altara. {decision.institution_name} will continue the process through its normal channel.
      </div>
      <div className="mt-4 flex items-center gap-2 flex-wrap">
        <button
          onClick={confirm}
          disabled={busy}
          data-testid="act-ready-confirm-btn"
          className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-[13px] font-semibold text-white disabled:opacity-40"
          style={{ background: NAVY }}
        >
          {busy ? "Recording…" : "Confirm I'm ready to continue"} <ArrowRight className="w-3.5 h-3.5" />
        </button>
        <button
          onClick={onCancel}
          disabled={busy}
          data-testid="act-ready-back"
          className="inline-flex items-center gap-1 rounded-full px-4 py-2 text-[12px] font-semibold border border-slate-200 bg-white"
          style={{ color: NAVY }}
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back
        </button>
      </div>
      {err && <div className="text-[12px] text-red-700 mt-2" data-testid="act-ready-error">{err}</div>}
    </div>
  );
}

/* ─────────────────── option 2 · More info topics ─────────────────── */
function MoreInfoTopics({ decision, deviceId, onCancel, onAdvance }) {
  const [picked, setPicked]   = useState(() => new Set());
  const [note, setNote]       = useState("");
  const [busy, setBusy]       = useState(false);
  const [err, setErr]         = useState(null);

  function toggle(id) {
    setPicked(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }
  const somethingElse = picked.has("SOMETHING_ELSE");

  async function submit() {
    if (!picked.size || busy) return;
    setBusy(true); setErr(null);
    try {
      const next = await recordAct(decision.decision_id, deviceId, {
        choice: CONSUMER_RESPONSE.MORE_INFO,
        more_info_topics: [...picked],
        more_info_note: somethingElse ? (note || null) : null,
      });
      onAdvance(next);
    } catch (e) { setErr(e?.response?.data?.detail || "Could not submit."); }
    finally { setBusy(false); }
  }

  return (
    <div className="rounded-2xl border border-slate-100 bg-white p-5" data-testid="act-more-info-topics">
      <div className="flex items-center gap-2">
        <HelpCircle className="w-5 h-5" style={{ color: GOLD }} />
        <div className="text-[10.5px] uppercase tracking-[0.24em] font-semibold" style={{ color: GOLD }}>
          More information requested
        </div>
      </div>
      <div className="mt-2 font-display text-[15px] font-semibold" style={{ color: NAVY }}>
        What would you like more information about?
      </div>
      <div className="text-[11.5px] text-slate-500 mt-1">Pick every topic that applies.</div>

      <ul className="mt-4 space-y-2" role="group" aria-label="More information topics">
        {MORE_INFO_TOPICS.map(t => {
          const checked = picked.has(t.id);
          return (
            <li key={t.id}>
              <button
                onClick={() => toggle(t.id)}
                data-testid={`more-info-topic-${t.id.toLowerCase()}`}
                aria-pressed={checked}
                className="w-full text-left flex items-center gap-3 rounded-xl p-3 border transition"
                style={{
                  borderColor: checked ? GOLD : "#E2E8F0",
                  background:  checked ? `${GOLD}12` : "white",
                }}
              >
                <span
                  className="w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0 border-2"
                  style={{
                    background:  checked ? GOLD : "white",
                    borderColor: checked ? GOLD : "#CBD5E1",
                  }}
                >
                  {checked && <CheckCircle2 className="w-3 h-3 text-white" />}
                </span>
                <span className="text-[13px] font-medium" style={{ color: NAVY }}>{t.label}</span>
              </button>
            </li>
          );
        })}
      </ul>

      {somethingElse && (
        <div className="mt-3" data-testid="more-info-note-wrap">
          <label htmlFor="more-info-note" className="text-[10.5px] uppercase tracking-widest font-semibold" style={{ color: GOLD }}>
            Something else <span className="text-slate-400 font-normal normal-case">(optional)</span>
          </label>
          <textarea
            id="more-info-note"
            data-testid="more-info-note-input"
            value={note}
            onChange={e => setNote(e.target.value.slice(0, 500))}
            rows={3}
            placeholder="Briefly describe what else you'd like to know."
            className="mt-1.5 w-full rounded-xl border border-slate-200 p-3 text-[12.5px] focus:outline-none focus:ring-2"
            style={{ color: NAVY }}
          />
        </div>
      )}

      <div className="mt-4 flex items-center gap-2 flex-wrap">
        <button
          onClick={submit}
          disabled={!picked.size || busy}
          data-testid="more-info-submit-btn"
          className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-[13px] font-semibold text-white disabled:opacity-40"
          style={{ background: NAVY }}
        >
          {busy ? "Submitting…" : "Submit response"} <ArrowRight className="w-3.5 h-3.5" />
        </button>
        <button
          onClick={onCancel}
          disabled={busy}
          data-testid="more-info-back"
          className="inline-flex items-center gap-1 rounded-full px-4 py-2 text-[12px] font-semibold border border-slate-200"
          style={{ color: NAVY }}
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back
        </button>
      </div>
      {err && <div className="text-[12px] text-red-700 mt-2" data-testid="more-info-error">{err}</div>}
    </div>
  );
}

/* ─────────────────── option 3 · Do not proceed reason ─────────────────── */
function DoNotProceedReason({ decision, deviceId, onCancel, onAdvance }) {
  const [reason, setReason] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  async function submit() {
    if (busy) return;
    setBusy(true); setErr(null);
    try {
      const payload = { choice: CONSUMER_RESPONSE.DO_NOT };
      if (reason) payload.do_not_proceed_reason = reason;
      const next = await recordAct(decision.decision_id, deviceId, payload);
      onAdvance(next);
    } catch (e) { setErr(e?.response?.data?.detail || "Could not record response."); }
    finally { setBusy(false); }
  }
  return (
    <div className="rounded-2xl border border-slate-100 bg-white p-5" data-testid="act-do-not-proceed">
      <div className="flex items-center gap-2">
        <XCircle className="w-5 h-5" style={{ color: "#B91C1C" }} />
        <div className="text-[10.5px] uppercase tracking-[0.24em] font-semibold" style={{ color: "#B91C1C" }}>
          Don't wish to proceed
        </div>
      </div>
      <div className="mt-2 font-display text-[15px] font-semibold" style={{ color: NAVY }}>
        Would you like to tell us why?
      </div>
      <div className="text-[11.5px] text-slate-500 mt-1">Optional. Your reason is not shared with the institution unless you say so.</div>

      <ul className="mt-4 space-y-2" role="radiogroup" aria-label="Reason for not proceeding">
        {DO_NOT_PROCEED_REASONS.map(r => {
          const checked = reason === r.id;
          return (
            <li key={r.id}>
              <button
                onClick={() => setReason(checked ? null : r.id)}
                data-testid={`do-not-reason-${r.id.toLowerCase()}`}
                aria-checked={checked}
                role="radio"
                className="w-full text-left flex items-center gap-3 rounded-xl p-3 border transition"
                style={{
                  borderColor: checked ? "#B91C1C" : "#E2E8F0",
                  background:  checked ? "#FEF2F2" : "white",
                }}
              >
                <span
                  className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 border-2"
                  style={{ borderColor: checked ? "#B91C1C" : "#CBD5E1", background: "white" }}
                >
                  {checked && <span className="w-2.5 h-2.5 rounded-full" style={{ background: "#B91C1C" }} />}
                </span>
                <span className="text-[13px] font-medium" style={{ color: NAVY }}>{r.label}</span>
              </button>
            </li>
          );
        })}
      </ul>

      <div className="mt-4 flex items-center gap-2 flex-wrap">
        <button
          onClick={submit}
          disabled={busy}
          data-testid="do-not-confirm-btn"
          className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-[13px] font-semibold text-white disabled:opacity-40"
          style={{ background: NAVY }}
        >
          {busy ? "Recording…" : "Confirm"} <ArrowRight className="w-3.5 h-3.5" />
        </button>
        <button
          onClick={onCancel}
          disabled={busy}
          data-testid="do-not-back"
          className="inline-flex items-center gap-1 rounded-full px-4 py-2 text-[12px] font-semibold border border-slate-200"
          style={{ color: NAVY }}
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back
        </button>
      </div>
      {err && <div className="text-[12px] text-red-700 mt-2" data-testid="do-not-error">{err}</div>}
    </div>
  );
}

/* ─────────────────── confirmation · More info recorded ─────────────────── */
function MoreInfoRecorded({ decision }) {
  const act    = decision.act || {};
  const topics = act.more_info_topics || act.response_reason || [];
  const labels = topics.map(id => MORE_INFO_TOPICS.find(t => t.id === id)?.label || id);
  return (
    <section className="space-y-4" data-testid="act-more-info-recorded">
      <div className="rounded-2xl border p-6" style={{ background: `${GOLD}0F`, borderColor: `${GOLD}55` }}>
        <div className="flex items-center gap-2">
          <CheckCircle2 className="w-5 h-5" style={{ color: "#059669" }} />
          <div className="text-[10.5px] uppercase tracking-[0.24em] font-semibold" style={{ color: "#059669" }}>
            Your response has been recorded
          </div>
        </div>
        <div className="mt-2 font-display text-2xl font-semibold" style={{ color: NAVY }}>
          {decision.proposal_name}
        </div>
        <div className="text-[13px] text-slate-700 mt-2">
          You asked for more information
        </div>
        <div className="mt-3">
          <div className="text-[10.5px] uppercase tracking-widest font-semibold text-slate-500 mb-1.5">About</div>
          <ul className="space-y-1">
            {labels.map(l => (
              <li key={l} className="flex items-center gap-2 text-[13.5px] font-semibold" style={{ color: NAVY }}>
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: GOLD }} />
                {l}
              </li>
            ))}
          </ul>
          {act.more_info_note && (
            <div className="mt-2 text-[12px] text-slate-600 italic">"{act.more_info_note}"</div>
          )}
        </div>
        <div className="text-[12.5px] text-slate-700 mt-4 leading-relaxed">
          {decision.institution_name} can continue the journey through its normal process.
        </div>
        <div className="mt-3 rounded-lg p-3 flex items-start gap-2" style={{ background: "white", border: "1px solid #E2E8F0" }} data-testid="more-info-boundary-note">
          <Shield className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: NAVY }} />
          <div className="text-[11.5px] text-slate-600">Nothing has been accepted or signed through Altara.</div>
        </div>

        <div className="mt-5 flex items-center gap-2 flex-wrap">
          <Link
            to="/wallet/decisions"
            data-testid="more-info-return-btn"
            className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-[13px] font-semibold text-white"
            style={{ background: NAVY }}
          >
            Return to My Decisions <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>

      <div className="text-[11px] text-slate-500 text-center" data-testid="more-info-status-hint">
        Status recorded: <span className="font-semibold" style={{ color: NAVY }}>More information requested</span> · you've asked for more information before deciding.
      </div>
    </section>
  );
}

/* ─────────────────── completed view · READY / DO_NOT ─────────────────── */
function CompletedView({ decision, deviceId }) {
  const act = decision.act || {};
  const oe  = decision.outcome_evidence;
  const isReady = act.consumer_response === CONSUMER_RESPONSE.READY;
  const color   = isReady ? "#059669" : "#B91C1C";
  const label   = isReady ? "Ready to proceed" : "Don't wish to proceed";
  const banner  = isReady
    ? `You've indicated you're ready to continue with ${decision.institution_name}.`
    : `You've indicated you don't want to continue with this proposal.`;
  return (
    <section className="space-y-4" data-testid="act-completed">
      <div
        className="rounded-2xl border p-5"
        style={{ background: isReady ? "#ECFDF5" : "#FEF2F2", borderColor: isReady ? "#A7F3D0" : "#FECACA" }}
      >
        <div className="flex items-center gap-2">
          {isReady ? <CheckCircle2 className="w-5 h-5" style={{ color }} /> : <XCircle className="w-5 h-5" style={{ color }} />}
          <div className="text-[10.5px] uppercase tracking-[0.24em] font-semibold" style={{ color }}>
            Your response has been recorded · {label}
          </div>
        </div>
        <div className="mt-2 font-display text-[16px] font-semibold" style={{ color: NAVY }}>{banner}</div>
        {!isReady && act.response_reason && (
          <div className="text-[12px] text-slate-600 mt-1.5">
            Reason: <span className="font-semibold">{
              DO_NOT_PROCEED_REASONS.find(r => r.id === act.response_reason)?.label || act.response_reason
            }</span>
          </div>
        )}
        <div className="text-[12px] text-slate-700 mt-2">
          {decision.institution_name} continues its normal advice and contracting process through its own channels.
        </div>
        <div className="text-[11px] text-slate-500 mt-2">Recorded {new Date(act.recorded_at).toLocaleString()}</div>
        <div className="mt-3 rounded-lg p-3 flex items-start gap-2" style={{ background: "white", border: "1px solid #E2E8F0" }}>
          <Shield className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: NAVY }} />
          <div className="text-[11.5px] text-slate-600">Nothing has been accepted or signed through Altara.</div>
        </div>
      </div>

      {oe && (
        <div className="rounded-2xl border border-slate-100 bg-white p-5" data-testid="act-outcome-evidence-card">
          <div className="flex items-center gap-2">
            <FileText className="w-5 h-5" style={{ color: GOLD }} />
            <div className="font-display text-[15px] font-semibold" style={{ color: NAVY }}>Consumer Outcome Evidence</div>
          </div>
          <div className="text-[11.5px] text-slate-600 mt-1.5 leading-relaxed">
            A hash-chained evidence record of your journey through Trust, Understand, Check and Act — held by Altara for your records. This is not a certificate of acceptance or contracting.
          </div>
          <dl className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 text-[12px]">
            <Row k="Evidence ID"      v={oe.outcome_evidence_id} mono />
            <Row k="Issued at"        v={new Date(oe.issued_at).toLocaleString()} />
            <Row k="Specification"    v={oe.specification} />
            <Row k="Evidence events"  v={String(oe.evidence_trail?.length || 0)} />
            <Row k="Evidence hash"    v={(oe.evidence_hash_chain || "").slice(0, 24) + "…"} mono span2 />
          </dl>
          <div className="mt-4 flex items-center gap-2 flex-wrap">
            <Link
              to={`/wallet/decisions/${decision.decision_id}/outcome-evidence`}
              data-testid="act-outcome-view"
              className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-[12px] font-semibold text-white"
              style={{ background: NAVY }}
            >
              <FileText className="w-3.5 h-3.5" /> View / print evidence
            </Link>
            <a
              href={`${process.env.REACT_APP_BACKEND_URL || ""}/api/wallet/decisions/${decision.decision_id}/outcome-evidence?device_id=${encodeURIComponent(deviceId)}`}
              target="_blank" rel="noreferrer"
              data-testid="act-outcome-json"
              className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-[12px] font-semibold border border-slate-200"
              style={{ color: NAVY }}
            >
              <ExternalLink className="w-3.5 h-3.5" /> Download JSON
            </a>
          </div>
        </div>
      )}
    </section>
  );
}

/* ─────────────────── small helpers ─────────────────── */
function BoundaryNotice() {
  return (
    <div className="rounded-xl p-3 flex items-start gap-2 text-[11px]" style={{ background: "#F1F5F9" }} data-testid="act-boundary-notice">
      <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" style={{ color: NAVY }} />
      <div className="text-slate-600 leading-relaxed">
        Altara records your journey and your intended next step. It does not accept, sign, execute or process this financial product on your behalf.
      </div>
    </div>
  );
}

function Row({ k, v, mono, span2 }) {
  return (
    <div className={`flex items-baseline gap-2 ${span2 ? "sm:col-span-2" : ""}`}>
      <dt className="text-[10.5px] uppercase tracking-widest text-slate-400 flex-shrink-0 w-28">{k}</dt>
      <dd className={`truncate ${mono ? "font-mono text-[11px]" : ""}`} style={{ color: NAVY }}>{v}</dd>
    </div>
  );
}
